import { T, Var } from 'gt-react';
import { format } from 'date-fns';
import { Link } from '@tanstack/react-router';
import { WarningDiamondIcon } from '@phosphor-icons/react';
import type { APInvoiceExceptionView } from '@erp/rpc';
import { useAPInvoiceExceptions } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { partitionInvoiceExceptions } from './partitionInvoiceExceptions';

/**
 * Read-only open (and resolved) exceptions on the invoice slide-in.
 * Clearing / resolving from here is intentionally not offered.
 */
export function InvoiceExceptionsSection({ invoiceId }: { invoiceId: string }) {
  const { exceptions, isLoading, error } = useAPInvoiceExceptions(invoiceId);
  const { open, resolved } = partitionInvoiceExceptions(exceptions);

  if (!isLoading && !error && exceptions.length === 0) return null;

  return (
    <section
      className={
        open.length > 0
          ? 'space-y-2 rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20'
          : 'space-y-2 rounded-md border p-3'
      }
    >
      <h3 className="flex items-center gap-1.5 text-sm font-medium">
        <WarningDiamondIcon className="size-4 text-amber-700 dark:text-amber-400" />
        <T context="invoice slide-in: heading over persisted exception rows">
          Exceptions
        </T>
        {open.length > 0 && (
          <Badge variant="destructive" className="ml-1">
            {open.length}
          </Badge>
        )}
      </h3>
      {isLoading && (
        <p className="text-xs text-muted-foreground">
          <T context="invoice slide-in: exceptions section loading">
            Loading exceptions…
          </T>
        </p>
      )}
      {error && (
        <p className="text-xs text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </p>
      )}
      {open.length > 0 && (
        <ul className="space-y-2">
          {open.map((exception) => (
            <ExceptionRow key={exception.id} exception={exception} />
          ))}
        </ul>
      )}
      {resolved.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="text-xs text-muted-foreground hover:text-foreground">
            <T context="invoice slide-in: toggle listing resolved exceptions">
              Resolved (<Var>{resolved.length}</Var>)
            </T>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="mt-2 space-y-2 opacity-80">
              {resolved.map((exception) => (
                <ExceptionRow key={exception.id} exception={exception} />
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}

function ExceptionRow({ exception }: { exception: APInvoiceExceptionView }) {
  const detected = format(new Date(exception.detectedAt), 'MMM d, yyyy');
  return (
    <li className="rounded-md border bg-background p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{exception.name}</span>
        {exception.status === 'resolved' ? (
          <Badge variant="outline">
            <T context="badge on a resolved AP invoice exception">Resolved</T>
          </Badge>
        ) : exception.ownerName ? (
          <span className="text-xs text-muted-foreground">
            {exception.ownerName}
          </span>
        ) : exception.ownerType ? (
          <Badge variant="outline">{exception.ownerType}</Badge>
        ) : (
          <Badge variant="outline" className="text-amber-600">
            <T context="badge when an invoice exception has no owner">
              Unassigned
            </T>
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">{detected}</span>
      </div>
      {exception.reasoning && (
        <p className="mt-1 text-xs text-muted-foreground">
          {exception.reasoning}
        </p>
      )}
      {exception.taskId && (
        <Link
          to="/tasks/$taskId"
          params={{ taskId: exception.taskId }}
          className="mt-1 inline-block text-xs text-primary hover:underline"
        >
          <T context="link from an invoice exception to its hand-off task">
            Open task
          </T>
        </Link>
      )}
    </li>
  );
}
